import {
  AppointmentType,
  CalendarType,
  DayOfWeek,
  QueryFactory,
  ResourceDto,
  ResourceType,
} from '@lmt/shared-types';
import LeftArrowGray from '@lmt/unicorn-shared/app/icons/arrow-left-grey.svg?react';
import LeftArrow from '@lmt/unicorn-shared/app/icons/arrow-left.svg?react';
import RightArrow from '@lmt/unicorn-shared/app/icons/arrow-right.svg?react';
import {
  localFormat,
  useLocale,
} from '@lmt/unicorn-shared/app/helpers/date-helper';
import { IconButton, useEventCallback } from '@mui/material';
import {
  DateCalendarSlotProps,
  DateCalendarSlots,
  PropsFromSlot,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import clsx from 'clsx';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  getISOWeek,
  isToday,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import endOfWeek from 'date-fns/endOfWeek';
import isSameDay from 'date-fns/isSameDay';
import isWithinInterval from 'date-fns/isWithinInterval';
import startOfWeek from 'date-fns/startOfWeek';
import React, { MouseEventHandler, useEffect, useMemo, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import styles from './MiniCalendar.module.scss';
import { useFirstDayOfWeek } from '../common';

class ExtendedAdapterDateFns extends AdapterDateFns {
  public getWeekNumber = (value: Date) => {
    return getISOWeek(value);
  };
}

export type Props = {
  selectedDate: Date;
  onDateChange?: (date: Date) => void;
  mainCalendarType: CalendarType;
  currentMonthDate: Date;
  onMonthChange?: (date: Date) => void;
  hideMonthArrows?: boolean;
  disableMonthSwitchingByClickingOnDates?: boolean;
  isShowingOccupancy?: boolean;
  isShowingSelectedOccupancy?: boolean;
  places?: number[];
  headerDateFormat?: string;
  disableMonthPicker?: boolean;
  isShowingAbsences?: boolean;
  displayWeekNumber?: boolean;
  absences?: {
    holidays: number[];
    resources: number[];
  };
  showDaysOutsideCurrentMonth?: boolean;
  handleMouseEnter?: (target: HTMLElement, date: Date | null) => void;
  onDayCellDoubleClick?: (date: Date) => void;
  // events for day cells
  onMouseOver?: MouseEventHandler<HTMLElement>;
  onMouseLeave?: MouseEventHandler<HTMLElement>;
};

interface CustomCalendarHeaderProps
  extends PropsFromSlot<DateCalendarSlots<Date>['calendarHeader']> {
  headerDateFormat: string;
  hideMonthArrows: boolean;
  onHeaderClick: () => void;
}

interface CustomDayComponentProps
  extends PropsFromSlot<DateCalendarSlots<Date>['day']>,
    Partial<Props> {
  firstDayOfWeekIndex: DayOfWeek;
  placesPerDay: Record<number, number>;
  placesNumber: number;
  onMouseOver?: MouseEventHandler<HTMLElement>;
  onMouseLeave?: MouseEventHandler<HTMLElement>;
}

/*
  This component should not be used directly from outside.
  You should use either MiniCalendar or ThreeMonthMiniCalendar.
 */
export const MiniCalendarInternal: React.FC<Props> = React.memo(
  function MiniCalendar(props) {
    const {
      currentMonthDate,
      selectedDate,
      onDateChange,
      isShowingOccupancy,
      headerDateFormat,
      disableMonthPicker,
      hideMonthArrows,
    } = props;

    // result is an object with Key=ResourceId, Object=Resource
    const placesQuery = QueryFactory.ResourceQuery.useGetResourcesQuery(
      { includeDeleted: false, resourceType: ResourceType.Places },
      {
        select: (data) => {
          const placesDictionary: Record<number, ResourceDto> = {};
          (data?.data ?? [])
            .filter((x) =>
              props.isShowingSelectedOccupancy
                ? (props.places ?? []).includes(x.id)
                : true,
            )
            .forEach((resource) => {
              placesDictionary[resource.id] = resource;
            });
          return placesDictionary;
        },
      },
    );

    const placesNumber = placesQuery.data
      ? Object.keys(placesQuery.data).length
      : 0;

    // We request two more weeks to be sure to get all events
    // (even for days in previous/next month that are part of MiniCalendar for current month)
    const startMonthDate = addWeeks(startOfMonth(currentMonthDate), -1);
    const endMonthDate = addWeeks(endOfMonth(currentMonthDate), 1);
    const firstDayOfWeekIndex = useFirstDayOfWeek();
    const monthAppointments =
      QueryFactory.AppointmentEventsQuery.useSearchAppointmentsWithResourceOccupationQuery(
        {
          from: startMonthDate,
          to: endMonthDate,
          isWithoutCanceledVisits: true,
          eventTypes: [AppointmentType.Visit, AppointmentType.Absence],
        },
        {
          staleTime: 1000 * 120,
          gcTime: 1000 * 120,
          enabled: !!isShowingOccupancy,
        },
      );

    /**
     * Number of taken places (rooms) per day
     */
    const placesPerDay = useMemo(() => {
      if (
        !isShowingOccupancy ||
        !monthAppointments.data ||
        !placesNumber ||
        !placesQuery.data
      ) {
        return {};
      }
      const occupiedPlacesPerDay: Record<number, number[]> = {};
      for (
        let currentDay = startMonthDate;
        currentDay < endMonthDate;
        currentDay = addDays(currentDay, 1)
      ) {
        occupiedPlacesPerDay[currentDay.getTime()] = [];
      }

      monthAppointments.data.forEach((appointment) => {
        if (!appointment.resourceIds) {
          return;
        }
        // we only count appointments with at least one Place resource
        let isPlaceExist = false;
        const places = [];
        for (const resourceId of appointment.resourceIds) {
          if (placesQuery.data![resourceId]) {
            isPlaceExist = true;
            places.push(resourceId);
          }
        }
        if (!isPlaceExist) {
          return;
        }
        let date = startOfDay(appointment.start);
        while (date < startMonthDate) {
          date = addDays(date, 1);
        }
        const occupiedPlacePerDay = occupiedPlacesPerDay[date.getTime()];
        occupiedPlacePerDay?.push(...places);
        date = addDays(date, 2);
        while (appointment.end > date && date <= endMonthDate) {
          //if an event lasts the whole day we need to add it to that day as well
          occupiedPlacesPerDay[addDays(date, -1).getTime()]?.push(...places);
          date = addDays(date, 1);
        }
      });

      const numberOfPlacesPerDay: Record<number, number> = {};
      for (const key in occupiedPlacesPerDay) {
        numberOfPlacesPerDay[Number(key)] = new Set(
          occupiedPlacesPerDay[Number(key)],
        ).size;
      }
      return numberOfPlacesPerDay;
    }, [monthAppointments.data, placesQuery.data, isShowingOccupancy]);

    const handleDayChange = useEventCallback((date: Date | null) => {
      if (!onDateChange) {
        return;
      }
      if (date !== selectedDate) {
        onDateChange(date!);
      }
    });

    const onMonthChange = useEventCallback((date: Date | null) => {
      if (!date) return;
      props.onMonthChange?.(date);
    });
    const [showMonthPicker, setShowMonthPicker] = useState(false);

    const monthPickerChangeHandler = useEventCallback((date: Date | null) => {
      handleDayChange(date);
      setShowMonthPicker(false);
    });
    const locale = useLocale();

    const adapterLocale = useMemo(() => {
      return {
        ...locale,
        options: { weekStartsOn: firstDayOfWeekIndex },
      };
    }, [locale, firstDayOfWeekIndex]);

    const calendarHeaderProps = useMemo(() => {
      return {
        hideMonthArrows,
        headerDateFormat: headerDateFormat ?? 'LLL yyyy',
        onHeaderClick: () => {
          if (disableMonthPicker) {
            return;
          }
          setShowMonthPicker(true);
        },
      } as DateCalendarSlotProps<Date>['calendarHeader'];
    }, [hideMonthArrows, headerDateFormat, disableMonthPicker]);

    const dayComponentProps = useMemo(() => {
      return {
        placesPerDay,
        placesNumber,
        firstDayOfWeekIndex,
        ...props,
        onSelectedDayClick: (day: any) => {
          handleDayChange(day);
        },
      } as DateCalendarSlotProps<Date>['day'];
    }, [props, placesNumber, placesPerDay, firstDayOfWeekIndex]);

    return (
      <div className={styles.datePicker} data-test-id={'miniCalendar'}>
        {showMonthPicker ? (
          <>
            <div
              className={styles.yearBack}
              onClick={() => {
                setShowMonthPicker(false);
              }}
            >
              <LeftArrowGray
                className={styles.grayArrowIcon}
                data-test-id="miniCalendar-year-back"
              />
            </div>
            <ReactDatePicker
              selected={currentMonthDate}
              onChange={monthPickerChangeHandler}
              onSelect={monthPickerChangeHandler}
              showMonthYearPicker={true}
              showFourColumnMonthYearPicker={true}
              locale={locale}
              inline
            />
          </>
        ) : (
          <div
            data-test-id={`miniCalendar-${localFormat(currentMonthDate, 'LLLL')}`}
          >
            <LocalizationProvider
              dateAdapter={ExtendedAdapterDateFns}
              adapterLocale={adapterLocale}
              localeText={{
                calendarWeekNumberHeaderText: '№',
              }}
            >
              <StaticDatePicker
                value={currentMonthDate}
                onMonthChange={onMonthChange}
                onChange={handleDayChange}
                dayOfWeekFormatter={dateOfWeekFormatter}
                slots={{
                  calendarHeader:
                    CalendarHeaderComponent as DateCalendarSlots<Date>['calendarHeader'],
                  day: WeekDayComponent as DateCalendarSlots<Date>['day'],
                  actionBar: EmptyComponent,
                }}
                slotProps={{
                  calendarHeader: calendarHeaderProps,
                  day: dayComponentProps,
                  toolbar: {
                    hidden: true,
                  },
                }}
                displayWeekNumber={props.displayWeekNumber}
              />
            </LocalizationProvider>
          </div>
        )}
      </div>
    );
  },
);

/*
  This component should be used outside if you want to add it to Calendar
 */
export const MiniCalendar: React.FC<
  Omit<Props, 'currentMonthDate' | 'onMonthChange' | 'hideMonthArrows'>
> = (props) => {
  const [currentMonthDate, setCurrentMonthDate] = useState(props.selectedDate);
  useEffect(
    function onSelectedDateChanged() {
      // update date in MiniCalendar according to selected month in Calendar
      const selectedDateMonthStart = startOfMonth(props.selectedDate);
      if (startOfMonth(currentMonthDate) != selectedDateMonthStart) {
        setCurrentMonthDate(selectedDateMonthStart);
      }
    },
    [props.selectedDate],
  );
  return (
    <MiniCalendarInternal
      {...props}
      currentMonthDate={currentMonthDate}
      onMonthChange={setCurrentMonthDate}
    />
  );
};

function CalendarHeaderComponent(props: CustomCalendarHeaderProps) {
  const {
    currentMonth,
    onMonthChange,
    onHeaderClick,
    headerDateFormat,
    hideMonthArrows,
  } = props;

  return (
    <div
      className={styles.header}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onHeaderClick();
      }}
    >
      <span
        className={styles.headerMonth}
        data-test-id={'miniCalendar-currentDate'}
      >
        {localFormat(props.currentMonth, headerDateFormat ?? 'LLL yyyy')}
      </span>
      {!hideMonthArrows && (
        <div className={styles.headerArrows}>
          <IconButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onMonthChange(addMonths(currentMonth, -1), 'right');
            }}
          >
            <LeftArrow
              className={styles.arrowIcon}
              data-test-id="miniCalendar-leftArrow"
            />
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onMonthChange(addMonths(currentMonth, 1), 'left');
            }}
          >
            <RightArrow
              className={styles.arrowIcon}
              data-test-id="miniCalendar-rightArrow"
            />
          </IconButton>
        </div>
      )}
    </div>
  );
}

function WeekDayComponent(props: CustomDayComponentProps) {
  const {
    day,
    selectedDate,
    mainCalendarType,
    firstDayOfWeekIndex,
    placesPerDay,
    absences,
    isShowingOccupancy,
    isShowingAbsences,
    placesNumber,
    handleMouseEnter,
    onDayCellDoubleClick,
  } = props;
  if (!day || !selectedDate) return <></>;

  let needToHighlight;
  let isFirstDay;
  let isLastDay;
  if (mainCalendarType === CalendarType.Day) {
    needToHighlight = isSameDay(day, selectedDate);
    isFirstDay = needToHighlight;
    isLastDay = needToHighlight;
  } else {
    const options = {
      weekStartsOn: firstDayOfWeekIndex,
    };
    const start = startOfWeek(selectedDate, options);
    const end = endOfWeek(selectedDate, options);
    needToHighlight = isWithinInterval(day, { start, end });
    isFirstDay = isSameDay(day, start);
    isLastDay = isSameDay(day, end);
  }
  const today = isToday(day);
  const dayTimeInMillis = day.getTime();

  const wrapperClassNames = clsx(styles.dayWrapper, {
    [styles.highlightedDay]: needToHighlight,
    [styles.firstHighlightedDay]: isFirstDay,
    [styles.lastHighlightedDay]: isLastDay,
    [styles.currentMonthDay]: !props.outsideCurrentMonth,
    [styles.notCurrentMonthDay]: props.outsideCurrentMonth,
    [styles.today]: today,
    [styles.absence]: absences?.resources?.includes(dayTimeInMillis),
    [styles.holiday]: absences?.holidays?.includes(dayTimeInMillis),
  });
  const placesPerCurrentDay = placesPerDay[day.getTime()];
  const colorIndicator: undefined | 'red' | 'green' | 'yellow' =
    !isShowingOccupancy ||
    placesPerCurrentDay === 0 ||
    !placesPerCurrentDay ||
    !placesNumber
      ? undefined
      : placesPerCurrentDay >= placesNumber
        ? 'red'
        : placesPerCurrentDay >= 0.5 * placesNumber
          ? 'yellow'
          : 'green';
  return (
    <div
      key={dayTimeInMillis}
      className={wrapperClassNames}
      onClick={
        props.showDaysOutsideCurrentMonth || !props.outsideCurrentMonth
          ? () => {
              // this is to handle click on already selected day
              if (
                props.selected ||
                props.disableMonthSwitchingByClickingOnDates
              ) {
                (props as any).onSelectedDayClick?.(day);
              }
              if (props.disableMonthSwitchingByClickingOnDates) {
                return;
              }
              props.onDaySelect(day);
            }
          : undefined
      }
      onDoubleClick={() => {
        onDayCellDoubleClick?.(day);
      }}
      onMouseOver={props.onMouseOver}
      onMouseLeave={props.onMouseLeave}
      onMouseEnter={(e) => {
        const dateTimeInMillis = day.getTime();
        const target = e.currentTarget.parentElement;
        if (
          !isShowingAbsences ||
          (!absences?.resources.includes(dateTimeInMillis) &&
            !absences?.holidays.includes(dateTimeInMillis))
        ) {
          if (target) handleMouseEnter?.(target, null);
          return;
        }

        if (target) handleMouseEnter?.(target, day);
      }}
    >
      <IconButton className={styles.day} data-color={colorIndicator}>
        <span>{localFormat(day, 'd')}</span>
      </IconButton>
    </div>
  );
}

const EmptyComponent = () => <></>;

const dateOfWeekFormatter = (date: Date) => localFormat(date, 'eeeeee');
